import { navigate } from '@/app/router';
import { POSES } from '@/content/articulation';
import { DIAGRAMS } from '@/content/diagrams';
import { floors } from '@/content/floors';
import { debugStore, useDebug } from '@/game/debug';
import { markIntroSeen } from '@/game/intro';
import { cx } from '@/lib/cx';
import s from './debug.module.css';
import { Card } from './ui';

/** A page of the app with development switches in its address (`?model=…`, `?mic=sim`, …). */
const withParams = (params: string, hash = '#/') => `${window.location.pathname}?${params}${hash}`;

/** Every screen and development view, and the switches that change how a level runs. */
export function ScreensPanel() {
  const debug = useDebug();
  const simulated = new URLSearchParams(window.location.search).get('mic') === 'sim';
  return (
    <>
      <Card title="Level tools">
        <label className={s.check}>
          <input
            type="checkbox"
            checked={debug}
            onChange={(e) => {
              debugStore.set(e.target.checked);
            }}
          />
          Debug bar in levels (mark an attempt correct or missed, silence, end the mission)
        </label>
        <p className={s.hint}>
          Remembered on this device. <span className={s.mono}>?debug</span> in the address turns it
          on for one visit.
          {simulated && ' The microphone is simulated in this visit.'}
        </p>
      </Card>

      <Card title="Screens">
        <div className={s.row}>
          <button
            type="button"
            className={s.btn}
            onClick={() => {
              navigate({ page: 'intro' });
            }}
          >
            Intro (briefing)
          </button>
          <button
            type="button"
            className={s.btn}
            onClick={() => {
              markIntroSeen();
              navigate({ page: 'elevator' });
            }}
          >
            Foe Elevator
          </button>
          <a className={s.btn} href={withParams('audiotest')}>
            Sound check
          </a>
        </div>
      </Card>

      <Card title="Missions" flush>
        <div className={s.tableWrap}>
          <table className={cx(s.table, s.stack)}>
            <thead>
              <tr>
                <th>Floor</th>
                <th>Words</th>
                <th>
                  <span className={s.visuallyHidden}>Open</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {floors.map((floor) => (
                <tr key={floor.id}>
                  <td>
                    <strong>{floor.number}.</strong> {floor.foe.codename}
                  </td>
                  <td
                    data-label="Words"
                    className={cx(s.mono, s.truncate)}
                    title={floor.words.map((w) => w.text).join(', ')}
                  >
                    {floor.words.map((w) => w.text).join(', ')}
                  </td>
                  <td>
                    <div
                      className={s.row}
                      style={{ flexWrap: 'nowrap', justifyContent: 'flex-end' }}
                    >
                      <button
                        type="button"
                        className={cx(s.btn, s.small, s.primary)}
                        onClick={() => {
                          navigate({ page: 'floor', floorId: floor.id, run: String(Date.now()) });
                        }}
                      >
                        Play
                      </button>
                      <button
                        type="button"
                        className={cx(s.btn, s.small)}
                        title="Recorded children's speech instead of the microphone"
                        onClick={() => {
                          window.location.href = withParams(
                            'mic=sim&debug',
                            `#/floor/${floor.id}/${String(Date.now())}`,
                          );
                        }}
                      >
                        Simulated mic
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className={s.grid2}>
        <Card title="3D tongue model">
          <div className={s.row}>
            {Object.keys(POSES).map((pose) => (
              <a key={pose} className={cx(s.btn, s.small)} href={withParams(`model=${pose}`)}>
                {pose}
              </a>
            ))}
          </div>
        </Card>
        <Card title="Hint cards">
          <div className={s.row}>
            {Object.keys(DIAGRAMS).map((id) => (
              <a key={id} className={cx(s.btn, s.small)} href={withParams(`hint=${id}`)}>
                {id}
              </a>
            ))}
          </div>
        </Card>
      </div>
    </>
  );
}
